import { NeuralClassifier, TrainingSet, arrayToFeatureVector, clearLabelCorpus, dspConfig, dspFingerprint, loadModel, loadTrainingSet, pickBurstFrames, qualifiedLabel, saveLabelCorpus, saveModel, saveTrainingSet, } from "@beatbox/voice-core";
import { store } from "../app/state/store.js";
import { getActiveLibrary, refreshTrainedFlags } from "../storage/libraries.js";
import { markPhonemeTrained } from "../storage/phonemes.js";
import { clusterIdOf } from "./word-set.js";
/**
 * In-session phone calibration for Word Rehearsal (idea.md §8.3.1
 * "calibrate the scaffolds deliberately"). Runs on the panel's own feature
 * stream (no engine worklet needed): the operator holds a chip while the
 * child sustains a sound, or taps a chip as a pop is made; frames go into
 * the speaker's shared corpus and the classifier retrains immediately.
 *
 * Everything here is per-speaker training data — never a verdict. Quiet
 * frames are harvested opportunistically as the `silence` reject class so
 * a single calibrated sound is already enough to train a model.
 */
export const SILENCE = "silence";
const TAP_WINDOW_MS = 250;
const RECENT_MS = 2000;
const HOLD_PAD_MS = 100;
const MAX_NEGATIVES = 480;
const SILENCE_FLOOR = 120;
export class RehearsalCalibrator {
    constructor() {
        Object.defineProperty(this, "corpus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new TrainingSet()
        });
        Object.defineProperty(this, "clf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "libraryId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "default"
        });
        Object.defineProperty(this, "negatives", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "recent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "hold", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "lastTapAt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: -Infinity
        });
        Object.defineProperty(this, "busy", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /** Labels that use pitch to discriminate (default is pitch-invariant). */
        Object.defineProperty(this, "pitchVariant", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "impulse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        /** Fired whenever the corpus, model, or training state changes. */
        Object.defineProperty(this, "onChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
    }
    get classifier() {
        return this.clf?.ready ? this.clf : null;
    }
    get isTraining() {
        return this.busy;
    }
    get holdingLabel() {
        return this.hold?.label ?? null;
    }
    get holdFrames() {
        return this.hold?.frames.length ?? 0;
    }
    labels() {
        return this.corpus.labels();
    }
    countFor(label) {
        return this.corpus.countFor(label);
    }
    /** Declare kind/pitch hints for the phones of a (new) word set. */
    setWordSet(phones) {
        for (const p of phones) {
            const cid = clusterIdOf(p);
            if (!cid)
                continue;
            if (p.pitchInvariant === false)
                this.pitchVariant.add(cid);
            if (p.kind === "percussive")
                this.impulse.add(cid);
        }
    }
    /**
     * Rehydrate the persisted corpus + model. A corpus captured under a
     * different DSP fingerprint is ignored (feature-space integrity, §3.3);
     * a model without a matching corpus is not trusted either.
     */
    async load(phones) {
        try {
            const lib = await getActiveLibrary();
            this.libraryId = lib.id;
            for (const p of lib.phonemes) {
                if (p.pitchInvariant === false)
                    this.pitchVariant.add(p.id);
                if (p.percussive)
                    this.impulse.add(p.id);
            }
        }
        catch (err) {
            console.warn("[rehearsal-cal] failed to load active library", err);
        }
        this.setWordSet(phones);
        const liveFp = dspFingerprint(dspConfig);
        let fingerprintOk = true;
        try {
            const saved = await loadTrainingSet();
            if (saved) {
                if (saved.dspFingerprint !== null && saved.dspFingerprint !== liveFp) {
                    fingerprintOk = false;
                    console.warn("[rehearsal-cal] ignoring persisted corpus/model captured under " +
                        `DSP config '${saved.dspFingerprint}' (live '${liveFp}'); ` +
                        "recalibrate under the current config");
                }
                else {
                    const ts = TrainingSet.fromJSON(saved.data);
                    for (const { label, features } of ts.toFrames()) {
                        this.corpus.add(label, arrayToFeatureVector(features));
                    }
                    console.info("[rehearsal-cal] rehydrated corpus " +
                        `(${this.corpus.labels().join(",") || "empty"})`);
                }
            }
            if (fingerprintOk) {
                const model = await loadModel();
                if (model && model.labels.length >= 2) {
                    const c = new NeuralClassifier();
                    await c.load(model);
                    if (c.ready) {
                        this.clf = c;
                        console.info("[rehearsal-cal] applied persisted model " +
                            `(labels: ${model.labels.join(",")})`);
                    }
                }
                if (!this.clf && this.corpus.labels().length >= 2) {
                    await this.retrain();
                }
            }
        }
        catch (err) {
            console.warn("[rehearsal-cal] load failed", err);
        }
        this.onChange?.();
    }
    /**
     * Feed every live analysis frame. Frames go to the active hold if one is
     * in progress; otherwise quiet frames refresh the reject-class pool and a
     * short ring is kept so a tap can look back ~250 ms for the pop.
     */
    observe(f, nowMs) {
        this.recent.push({ t: nowMs, f });
        const cutoff = nowMs - RECENT_MS;
        while (this.recent.length > 0 && this.recent[0].t < cutoff) {
            this.recent.shift();
        }
        if (this.hold) {
            this.hold.frames.push({ t: nowMs, f });
            return;
        }
        if ((f.energy ?? 0) < dspConfig.energyGate) {
            this.negatives.push(f);
            if (this.negatives.length > MAX_NEGATIVES)
                this.negatives.shift();
        }
    }
    /** Start a press-and-hold capture for a sustained sound (or `silence`). */
    beginHold(label) {
        if (this.hold || this.busy)
            return false;
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        this.hold = { label, frames: [], startedAt: now };
        console.info(`[rehearsal-cal] hold START '${label}'`);
        return true;
    }
    cancelHold() {
        if (this.hold)
            console.info(`[rehearsal-cal] hold CANCEL '${this.hold.label}'`);
        this.hold = null;
    }
    /** Release the hold: trim the press/release edges and commit. */
    async endHold() {
        const h = this.hold;
        this.hold = null;
        if (!h)
            return null;
        const frames = h.frames;
        const t0 = (frames[0]?.t ?? h.startedAt) + HOLD_PAD_MS;
        const t1 = (frames[frames.length - 1]?.t ?? h.startedAt) - HOLD_PAD_MS;
        let kept = frames.filter((r) => r.t >= t0 && r.t <= t1).map((r) => r.f);
        if (kept.length < 3)
            kept = frames.map((r) => r.f);
        if (h.label !== SILENCE) {
            // A hold with no sound in it would poison the label with quiet.
            kept = kept.filter((f) => (f.energy ?? 0) >= dspConfig.energyGate);
        }
        console.info(`[rehearsal-cal] hold END '${h.label}' frames=${frames.length} kept=${kept.length}`);
        if (kept.length < 3) {
            return {
                label: h.label,
                added: 0,
                total: this.countFor(h.label),
                trained: !!this.classifier,
                reason: h.label === SILENCE
                    ? "hold a little longer"
                    : "no sound heard — keep holding while the sound is being made",
            };
        }
        return this.commit(h.label, kept);
    }
    /** Tap capture for a percussive sound: the burst in the last 250 ms. */
    async tap(label, nowMs) {
        const sinceLast = nowMs - this.lastTapAt;
        this.lastTapAt = nowMs;
        const posLo = nowMs - TAP_WINDOW_MS;
        const negLo = posLo - Math.min(1000, Math.max(0, sinceLast));
        const positives = [];
        for (const r of this.recent) {
            if (r.t >= posLo) {
                positives.push(r.f);
            }
            else if (r.t >= negLo) {
                this.negatives.push(r.f);
                if (this.negatives.length > MAX_NEGATIVES)
                    this.negatives.shift();
            }
        }
        if (positives.length === 0) {
            return {
                label,
                added: 0,
                total: this.countFor(label),
                trained: !!this.classifier,
                reason: "no frames captured — make the pop exactly as you tap",
            };
        }
        const burst = pickBurstFrames(positives);
        return this.commit(label, burst.length > 0 ? burst : positives.slice(0, 2));
    }
    /** Forget a label's samples so it can be recalibrated from scratch. */
    async clearLabel(label) {
        this.corpus.clearLabel(label);
        try {
            await clearLabelCorpus(qualifiedLabel(this.libraryId, label));
        }
        catch (err) {
            console.warn("[rehearsal-cal] failed to clear per-label corpus", err);
        }
        void markPhonemeTrained(label, false);
        if (this.corpus.labels().length >= 2) {
            await this.retrain();
        }
        else {
            this.clf = null;
            try {
                await saveModel({
                    version: 1,
                    labels: [],
                    featureNames: [],
                    modelTopology: null,
                    weightSpecs: null,
                    weightDataB64: "",
                });
            }
            catch (err) {
                console.warn("[rehearsal-cal] failed to clear model", err);
            }
            await this.persist();
        }
        console.info(`[rehearsal-cal] cleared '${label}'`);
        this.onChange?.();
    }
    async commit(label, frames) {
        this.corpus.addMany(label, frames);
        void markPhonemeTrained(label);
        if (label !== SILENCE) {
            // Top up the reject class from recently observed quiet so even a
            // first calibrated sound has something to be told apart from.
            const silCount = this.corpus.countFor(SILENCE);
            const want = Math.min(this.negatives.length, Math.max(0, SILENCE_FLOOR - silCount));
            if (want > 0)
                this.corpus.addMany(SILENCE, this.negatives.slice(-want));
        }
        const trained = await this.retrain();
        this.onChange?.();
        return {
            label,
            added: frames.length,
            total: this.countFor(label),
            trained,
            reason: trained
                ? undefined
                : this.corpus.labels().length < 2
                    ? "one more sound (or a quiet hold) is needed before a model can train"
                    : "training failed — see console",
        };
    }
    async retrain() {
        const labels = this.corpus.labels();
        if (labels.length < 2) {
            await this.persist();
            return false;
        }
        this.busy = true;
        this.onChange?.();
        try {
            const s = store.gameSettings.get();
            const c = new NeuralClassifier({
                C: s.classifierC,
                gamma: s.classifierGamma,
                rejectConfidence: s.classifierRejectConfidence,
                testFraction: s.classifierTestFraction,
                impulseCBoost: s.classifierImpulseCBoost,
                impulseLabels: labels.filter((l) => this.impulse.has(l)),
            });
            c.setPitchInvariantLabels(labels.filter((l) => !this.pitchVariant.has(l)));
            await c.train(this.corpus.toFrames());
            if (!c.ready)
                return false;
            this.clf = c;
            const serialized = await c.serialize();
            if (serialized)
                await saveModel(serialized);
            await this.persist();
            console.info(`[rehearsal-cal] model retrained (labels: ${labels.join(",")})`);
            return true;
        }
        catch (err) {
            console.error("[rehearsal-cal] training failed", err);
            return false;
        }
        finally {
            this.busy = false;
        }
    }
    async persist() {
        const fp = dspFingerprint(dspConfig);
        try {
            const data = this.corpus.toJSON();
            await saveTrainingSet(data, fp);
            for (const [label, rows] of Object.entries(data)) {
                await saveLabelCorpus(qualifiedLabel(this.libraryId, label), rows, fp);
            }
            await refreshTrainedFlags(this.libraryId);
        }
        catch (err) {
            console.warn("[rehearsal-cal] persist failed", err);
        }
    }
}
